import {
  CodeOutlineIcon,
  CopyOutlineIcon,
  SettingsIcon,
} from '@carboncopyco/icons';
import type { TourShare } from '@carboncopyco/types';
import { Button, Loading, TooltipBubble } from '@carboncopyco/ui';
import {
  AnalyticsTrackEventName,
  createEmbeddedIframeCode,
  isFuture,
  useAnalytics,
} from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import type { ShareTourModalState } from '../../../Share';
import { ShareTourModal } from '../../../Share';
import {
  UpdateSecurityLevelWarningModal,
  shouldShowSecurityLevelWarning,
} from '../../../Share/UpdateSecurityLevelWarningModal';
import { useCopy } from '../../../utils';
import { useTourPageProps } from '../TourPageContext';
import { useShowtimeUrl } from '@carboncopyco/components';

type LinkActionsProps = {
  link: TourShare;
  onSubmit: (values: ShareTourModalState, link: TourShare) => Promise<void>;
  isLoading: boolean;
};
export const LinkActions = ({
  link,
  onSubmit,
  isLoading,
}: LinkActionsProps) => {
  const { trackEvent } = useAnalytics();
  const { copyText } = useCopy();
  const { organizationId } = useParams();
  const [showShareModal, setShowShareModal] = useBoolean();
  const { showtimeShareUrl } = useShowtimeUrl();

  const {
    options,
    options: {
      security,
      autoSyncUpdates,
      expiresAt,
      integrations,
      welcomeMatTitle,
      skipWelcome,
    },
    isActive,
  } = link;
  const showtimeUrl = showtimeShareUrl(link.token);
  const salesforceLeads = !!integrations?.find((int) => int === 'salesforce');
  const hubspotLeads = !!integrations?.find((int) => int === 'hubspot');
  const slackIntegration = !!integrations?.find((int) => int === 'slack');

  const shareData: ShareTourModalState = {
    salesforceLeads,
    hubspotLeads,
    slackIntegration,
    isActive,
    autoSyncUpdates,
    ...options,
    toggleExpiration: !!expiresAt && isFuture(expiresAt),
    skipWelcome,
    security: {
      ...security,
      level: security.level,
    },
    cover: skipWelcome
      ? { requireName: false, requireEmail: false }
      : options.welcomeMatConfig,
    welcomeMatTitle,
  };
  const location = 'tour_info_page_shares_tab_table_side_actions';

  const { toursShareAspectRatioEnabled } = useTourPageProps();
  const [
    showUpdateSecurityLevelWarning,
    {
      setTrue: setTrueShowUpdateSecurityLevelWarning,
      setFalse: setFalseShowUpdateSecurityLevelWarning,
    },
  ] = useBoolean(false);
  const [formValues, setFormValues] = useState<ShareTourModalState>();

  if (showUpdateSecurityLevelWarning) {
    return (
      <UpdateSecurityLevelWarningModal
        show={showUpdateSecurityLevelWarning}
        isLoading={isLoading}
        onConfirm={async () => {
          if (formValues) {
            await onSubmit(formValues, link);
            setShowShareModal.setFalse();
            setFalseShowUpdateSecurityLevelWarning();
          }
        }}
        onClose={setFalseShowUpdateSecurityLevelWarning}
      />
    );
  }
  const expired = expiresAt && !isFuture(expiresAt);
  const linkIsInactive = !link.isActive || !!expired;

  return (
    <div className="flex pl-4 gap-3">
      {isLoading && <Loading color="text-ds-primary" />}
      <TooltipBubble
        title={linkIsInactive ? 'This link is inactive' : 'Copy embedded code'}
        placement="top"
      >
        <Button
          data-testid="share-copy-link"
          aria-label="Copy link"
          disabled={linkIsInactive}
          dark
          icon={<CodeOutlineIcon />}
          onClick={() => {
            trackEvent(AnalyticsTrackEventName.TOUR_CLICK_COPY_EMBEDDED_CODE, {
              location,
            });
            copyText(
              `${createEmbeddedIframeCode(showtimeUrl)}`,
              'Link was copied to your clipboard'
            );
          }}
        />
      </TooltipBubble>
      <TooltipBubble
        title={linkIsInactive ? 'This link is inactive' : 'Copy link URL'}
        placement="top"
      >
        <Button
          data-testid="share-copy-link"
          aria-label="Copy link"
          disabled={linkIsInactive}
          dark
          icon={<CopyOutlineIcon />}
          onClick={() => {
            trackEvent(AnalyticsTrackEventName.TOUR_CLICK_COPY_LINK_URL, {
              location,
            });
            copyText(`${showtimeUrl}`, 'Link was copied to your clipboard');
          }}
        />
      </TooltipBubble>
      <TooltipBubble
        title="Share settings"
        placement="top"
      >
        <Button
          data-testid="share-settings"
          disabled={expired}
          aria-label="Share settings"
          dark
          icon={<SettingsIcon />}
          onClick={() => {
            trackEvent(AnalyticsTrackEventName.TOUR_CLICK_SHARE_SETTINGS, {
              location,
            });
            setShowShareModal.setTrue();
          }}
        />
      </TooltipBubble>
      {showShareModal && (
        <ShareTourModal
          organizationId={organizationId}
          toursShareAspectRatioEnabled={!!toursShareAspectRatioEnabled}
          yesLabel="Save changes"
          title="Edit link settings"
          initialValues={shareData}
          handleSubmit={async (values, { setSubmitting }) => {
            if (
              shouldShowSecurityLevelWarning(
                security.level,
                values.security.level
              )
            ) {
              setTrueShowUpdateSecurityLevelWarning();
              setFormValues(values);
              setSubmitting(false);
              return;
            }
            await onSubmit(values, link);
            setShowShareModal.setFalse();
            setFalseShowUpdateSecurityLevelWarning();
          }}
          open={showShareModal}
          onClose={setShowShareModal.setFalse}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};
